import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  Refresh,
  Download,
  Clear,
  Edit,
  Delete,
  Search,
  Save,
  Cancel,
  PhoneAndroid,
  AccountBalance,
  Description,
  PictureAsPdf,
  FileDownload,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getTransactions, updateTransaction, deleteTransaction } from '../api';
import { formatCurrency, formatDate, getTransactionTypeColor, truncateText } from '../utils/formatters';
import ExportUtils from '../utils/exportUtils';
import toast from 'react-hot-toast';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState({
    type: '',
    source_type: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    search: '',
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getTransactions(params);
      setTransactions(response.data || []);
      setRowCount(response.total || 0);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [paginationModel, filters]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      source_type: '',
      start_date: '',
      end_date: '',
      min_amount: '',
      search: '',
    });
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTransaction(null);
  };

  const handleEdit = (transaction) => {
    setEditingTransaction({ ...transaction });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingTransaction(null);
  };

  const handleEditSave = async () => {
    if (!editingTransaction) return;

    setSaving(true);
    try {
      const response = await updateTransaction(editingTransaction.id, editingTransaction);
      toast.success('Transaction updated successfully');
      setSnackbar({
        open: true,
        message: 'Transaction updated successfully',
        severity: 'success',
      });
      handleEditClose();
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
      setSnackbar({
        open: true,
        message: 'Failed to update transaction',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      await deleteTransaction(deleteId);
      toast.success('Transaction deleted successfully');
      setSnackbar({
        open: true,
        message: 'Transaction deleted successfully',
        severity: 'success',
      });
      handleDeleteClose();
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
      setSnackbar({
        open: true,
        message: 'Failed to delete transaction',
        severity: 'error',
      });
    }
  };

  // ============ EXPORT FUNCTIONALITY ============
  const handleExport = async (format = 'excel') => {
    if (transactions.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      const filename = `transactions_${new Date().toISOString().slice(0, 10)}`;
      let exportResult = false;

      switch (format) {
        case 'excel':
          exportResult = ExportUtils.exportToExcel(transactions, filename);
          break;
        case 'pdf':
          exportResult = ExportUtils.exportToPDF(transactions, 'Transaction Report');
          break;
        case 'csv':
          exportResult = ExportUtils.exportToCSV(transactions, filename);
          break;
        default:
          toast.error('Invalid export format');
      }

      if (exportResult) {
        toast.success(`${format.toUpperCase()} exported successfully! ${transactions.length} records`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  // ============ EXPORT ALL FUNCTIONALITY ============
  const handleExportAll = async (format = 'excel') => {
    setExporting(true);
    try {
      // Fetch all transactions without pagination
      const params = {
        limit: 10000,
        offset: 0,
        ...filters,
      };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getTransactions(params);
      const allData = response.data || [];

      if (allData.length === 0) {
        toast.error('No data to export');
        setExporting(false);
        return;
      }

      const filename = `all_transactions_${new Date().toISOString().slice(0, 10)}`;
      let exportResult = false;

      switch (format) {
        case 'excel':
          exportResult = ExportUtils.exportToExcel(allData, filename);
          break;
        case 'pdf':
          exportResult = ExportUtils.exportToPDF(allData, 'All Transactions Report');
          break;
        case 'csv':
          exportResult = ExportUtils.exportToCSV(allData, filename);
          break;
        default:
          toast.error('Invalid export format');
      }

      if (exportResult) {
        toast.success(`${format.toUpperCase()} exported successfully! ${allData.length} records`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const getAmount = (row) => {
    if (row.paid_in > 0) return `+${formatCurrency(row.paid_in)}`;
    if (row.withdrawn > 0) return `-${formatCurrency(row.withdrawn)}`;
    return formatCurrency(0);
  };

  const getAmountColor = (row) => {
    if (row.paid_in > 0) return 'success.main';
    if (row.withdrawn > 0) return 'error.main';
    return 'text.secondary';
  };

  // Get source type label with icon
  const getSourceTypeLabel = (sourceType) => {
    if (sourceType === 'mpesa') {
      return { label: 'M-Pesa', icon: <PhoneAndroid sx={{ fontSize: 14 }} />, color: 'success' };
    } else if (sourceType === 'bank') {
      return { label: 'Bank', icon: <AccountBalance sx={{ fontSize: 14 }} />, color: 'primary' };
    }
    return { label: 'Unknown', icon: null, color: 'default' };
  };

  const columns = [
    {
      field: 'source_type',
      headerName: 'Source',
      width: 100,
      renderCell: (params) => {
        const source = getSourceTypeLabel(params.value);
        return (
          <Chip
            icon={source.icon}
            label={source.label}
            size="small"
            color={source.color}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      field: 'receipt_no',
      headerName: 'Receipt No.',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'completion_time',
      headerName: 'Date & Time',
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 280,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {truncateText(params.value, 50)}
          </Typography>
          {params.row.is_merchant_payment && (
            <Chip
              label="Merchant"
              size="small"
              color="info"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          )}
          {params.row.is_paybill && (
            <Chip
              label="PayBill"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ mt: 0.5, ml: 0.5 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: getTransactionTypeColor(params.value),
            color: 'white',
          }}
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ color: getAmountColor(params.row), fontWeight: 'medium' }}
        >
          {getAmount(params.row)}
        </Typography>
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 130,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 150,
      renderCell: (params) => (
        <Tooltip title={params.value || 'No remarks'}>
          <Typography variant="body2" sx={{
            fontStyle: params.value ? 'normal' : 'italic',
            color: params.value ? 'inherit' : 'text.secondary'
          }}>
            {params.value ? truncateText(params.value, 20) : 'No remarks'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row)}
              color="info"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Transactions</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={<Refresh />}
            onClick={fetchTransactions}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
          <Tooltip title="Export current page to Excel">
            <span>
              <Button
                variant="outlined"
                startIcon={<Description />}
                onClick={() => handleExport('excel')}
                size="small"
                disabled={exporting || transactions.length === 0}
                color="success"
              >
                Excel
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export current page to PDF">
            <span>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => handleExport('pdf')}
                size="small"
                disabled={exporting || transactions.length === 0}
                color="error"
              >
                PDF
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export current page to CSV">
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleExport('csv')}
                size="small"
                disabled={exporting || transactions.length === 0}
                color="info"
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export all transactions with current filters">
            <span>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => handleExportAll('excel')}
                size="small"
                disabled={exporting}
                color="primary"
              >
                Export All
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {exporting && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <LinearProgress sx={{ mt: 1 }} />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            Exporting data... Please wait.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              placeholder="Search..."
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              label="Type"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Credit">Credit</MenuItem>
              <MenuItem value="Debit">Debit</MenuItem>
              <MenuItem value="Both">Both</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                name="source_type"
                value={filters.source_type}
                onChange={handleFilterChange}
                input={<OutlinedInput label="Source" />}
              >
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="mpesa">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneAndroid sx={{ fontSize: 18, color: '#4CAF50' }} />
                    M-Pesa
                  </Box>
                </MenuItem>
                <MenuItem value="bank">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalance sx={{ fontSize: 18, color: '#1976d2' }} />
                    Bank
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Start Date"
              name="start_date"
              type="date"
              value={filters.start_date}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="End Date"
              name="end_date"
              type="date"
              value={filters.end_date}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              label="Min Amount"
              name="min_amount"
              type="number"
              value={filters.min_amount}
              onChange={handleFilterChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              startIcon={<Clear />}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={transactions}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        {selectedTransaction && (
          <>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                  <Chip
                    icon={getSourceTypeLabel(selectedTransaction.source_type).icon}
                    label={getSourceTypeLabel(selectedTransaction.source_type).label}
                    color={getSourceTypeLabel(selectedTransaction.source_type).color}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Receipt No.</Typography>
                  <Typography variant="body1" gutterBottom>{selectedTransaction.receipt_no}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                  <Typography variant="body1" gutterBottom>{formatDate(selectedTransaction.completion_time)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Details</Typography>
                  <Typography variant="body1" gutterBottom>{selectedTransaction.details}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Transaction Type</Typography>
                  <Chip
                    label={selectedTransaction.transaction_type}
                    sx={{ backgroundColor: getTransactionTypeColor(selectedTransaction.transaction_type), color: 'white' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTransaction.paid_in > 0 ? (
                      <span style={{ color: 'green' }}>+{formatCurrency(selectedTransaction.paid_in)}</span>
                    ) : (
                      <span style={{ color: 'red' }}>-{formatCurrency(selectedTransaction.withdrawn)}</span>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Balance</Typography>
                  <Typography variant="body1" gutterBottom>{formatCurrency(selectedTransaction.balance)}</Typography>
                </Grid>
                {selectedTransaction.merchant_business_name && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Merchant Information</Typography>
                    <Typography variant="body1">
                      {selectedTransaction.merchant_business_name}
                      {selectedTransaction.merchant_business_id && ` (ID: ${selectedTransaction.merchant_business_id})`}
                    </Typography>
                    {selectedTransaction.merchant_account && (
                      <Typography variant="body2" color="text.secondary">Account: {selectedTransaction.merchant_account}</Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Remarks</Typography>
                  <Typography variant="body1" gutterBottom sx={{
                    fontStyle: selectedTransaction.remarks ? 'normal' : 'italic',
                    color: selectedTransaction.remarks ? 'inherit' : 'text.secondary'
                  }}>
                    {selectedTransaction.remarks || 'No remarks'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip label={selectedTransaction.transaction_status || 'Completed'} color="success" size="small" />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        {editingTransaction && (
          <>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Receipt No." value={editingTransaction.receipt_no || ''}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, receipt_no: e.target.value })} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Completion Time" type="datetime-local"
                    value={editingTransaction.completion_time ? editingTransaction.completion_time.slice(0, 16) : ''}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, completion_time: e.target.value })}
                    size="small" InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Details" value={editingTransaction.details || ''}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, details: e.target.value })}
                    size="small" multiline rows={2} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Paid In" type="number" value={editingTransaction.paid_in || 0}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, paid_in: parseFloat(e.target.value) || 0 })}
                    size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Withdrawn" type="number" value={editingTransaction.withdrawn || 0}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, withdrawn: parseFloat(e.target.value) || 0 })}
                    size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Balance" type="number" value={editingTransaction.balance || 0}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, balance: parseFloat(e.target.value) || 0 })}
                    size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Transaction Type" value={editingTransaction.transaction_type || 'Unknown'}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, transaction_type: e.target.value })}
                    size="small">
                    <MenuItem value="Credit">Credit</MenuItem>
                    <MenuItem value="Debit">Debit</MenuItem>
                    <MenuItem value="Both">Both</MenuItem>
                    <MenuItem value="Unknown">Unknown</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Transaction Status" value={editingTransaction.transaction_status || ''}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, transaction_status: e.target.value })}
                    size="small" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Remarks" value={editingTransaction.remarks || ''}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, remarks: e.target.value })}
                    size="small" multiline rows={3} placeholder="Add any remarks or notes about this transaction..." />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose} startIcon={<Cancel />}>Cancel</Button>
              <Button onClick={handleEditSave} variant="contained" color="primary" startIcon={<Save />} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this transaction? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;